import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const CONTENT = {
  es: {
    title: "Aviso de Privacidad",
    eyebrow: "Canales Franchise LLC · Plataforma de Capacitación",
    updated: "Última actualización: 30 de abril de 2026",
    intro: "Canales Franchise LLC (\"nosotros\", \"la empresa\") respeta la privacidad de los empleados, vendedores y miembros del equipo de dirección que utilizan esta plataforma de capacitación interna. Este aviso describe qué datos recolectamos, cómo los usamos y qué derechos tienes sobre ellos.",
    sections: [
      {
        h: "1. Información que recolectamos",
        p: "Para operar la plataforma recolectamos: (a) datos de cuenta — nombre, correo electrónico corporativo, rol (vendedor, manager, administrador) y número telefónico opcional; (b) datos de uso — cursos completados, calificaciones de exámenes, puntos acumulados, tiempo de sesión; (c) datos técnicos — dirección IP, tipo de navegador, fecha y hora de inicio de sesión; (d) contenido generado — respuestas a evaluaciones, conversaciones con el asistente IA de idiomas."
      },
      {
        h: "2. Cómo usamos tu información",
        p: "Usamos tus datos exclusivamente para: (a) autenticar tu acceso a la plataforma; (b) registrar tu progreso de capacitación y otorgar puntos/certificados; (c) generar reportes para tu manager directo y para la dirección; (d) enviar notificaciones por correo electrónico (Resend) y SMS (Twilio) sobre actividad relevante; (e) prevenir fraude, abuso y mantener la seguridad. NO compartimos tus datos con terceros para fines de marketing y NO vendemos información personal."
      },
      {
        h: "3. Notificaciones SMS",
        p: "La plataforma envía mensajes SMS transaccionales únicamente a tres (3) números pre-autorizados pertenecientes al equipo de dirección de Canales Franchise LLC. Los SMS se disparan automáticamente cuando: (1) un empleado inicia sesión, (2) un empleado completa un curso de capacitación, (3) un empleado alcanza una meta de puntos (cada 100 puntos). Los destinatarios consintieron explícitamente recibir estos avisos mediante acuerdo interno con la empresa. Los mensajes son 100% transaccionales — no contienen contenido publicitario ni promocional. Los destinatarios pueden cancelar respondiendo STOP al SMS en cualquier momento, o pidiendo al administrador que los retire de la lista en /admin/sms. Frecuencia estimada: 5-15 mensajes por destinatario por día hábil."
      },
      {
        h: "4. Compartición con terceros",
        p: "Compartimos información mínima y estrictamente operacional con los siguientes proveedores de servicios: (a) Resend (envío de correos electrónicos transaccionales); (b) Twilio (envío de SMS); (c) Anthropic / Google / OpenAI vía Emergent LLM (procesamiento de texto IA, sin entrenamiento sobre tus datos); (d) MongoDB (almacenamiento de datos en infraestructura segura). Cada uno opera bajo sus propios términos de privacidad y solo recibe los datos necesarios para cumplir su función."
      },
      {
        h: "5. Retención de datos",
        p: "Los datos de cuenta se conservan mientras tu cuenta esté activa. Las sesiones de inicio (cookies y tokens) caducan automáticamente entre 7 y 90 días. Los logs de SMS y correos se retienen por 12 meses para auditoría. Al desactivar tu cuenta puedes solicitar el borrado completo de tus datos personales (excepto registros de capacitación que la empresa requiera mantener por motivos de cumplimiento)."
      },
      {
        h: "6. Tus derechos",
        p: "Tienes derecho a: (a) acceder a tus datos almacenados; (b) corregirlos si están desactualizados o inexactos; (c) solicitar el borrado de tu cuenta; (d) oponerte al envío de SMS (responde STOP o pide al administrador retirarte). Para ejercer cualquier derecho contacta a: antonio@canalesfranchise.com."
      },
      {
        h: "7. Seguridad",
        p: "Aplicamos medidas técnicas razonables: contraseñas hasheadas con bcrypt, cookies HttpOnly + Secure, HTTPS obligatorio, rate-limiting contra ataques de fuerza bruta, audit logs de eventos sensibles. A pesar de estos esfuerzos, ningún sistema en línea es 100% impenetrable. En caso de brecha relevante notificaremos a los usuarios afectados sin demora indebida."
      },
      {
        h: "8. Cambios a este aviso",
        p: "Podemos actualizar este aviso de privacidad periódicamente. La fecha al inicio refleja la última revisión. Si los cambios son significativos te notificaremos por correo o dentro de la plataforma."
      },
      {
        h: "9. Contacto",
        p: "Si tienes preguntas sobre privacidad o quieres ejercer tus derechos, escribe a: Canales Franchise LLC · antonio@canalesfranchise.com"
      },
    ],
  },
  en: {
    title: "Privacy Policy",
    eyebrow: "Canales Franchise LLC · Training Platform",
    updated: "Last updated: April 30, 2026",
    intro: "Canales Franchise LLC (\"we\", \"the company\") respects the privacy of employees, salespeople and leadership team members who use this internal training platform. This notice describes what data we collect, how we use it, and what rights you have over it.",
    sections: [
      {
        h: "1. Information we collect",
        p: "To operate the platform we collect: (a) account data — name, corporate email, role (salesperson, manager, administrator) and optional phone number; (b) usage data — completed courses, exam scores, accumulated points, session time; (c) technical data — IP address, browser type, login date and time; (d) generated content — answers to evaluations, conversations with the AI language assistant."
      },
      {
        h: "2. How we use your information",
        p: "We use your data exclusively to: (a) authenticate your platform access; (b) record your training progress and award points/certificates; (c) generate reports for your direct manager and leadership; (d) send notifications via email (Resend) and SMS (Twilio) about relevant activity; (e) prevent fraud, abuse and maintain security. We do NOT share your data with third parties for marketing purposes and we do NOT sell personal information."
      },
      {
        h: "3. SMS Notifications",
        p: "The platform sends transactional SMS messages exclusively to three (3) pre-authorized phone numbers belonging to the Canales Franchise LLC leadership team. SMS are triggered automatically when: (1) an employee logs in, (2) an employee completes a training course, (3) an employee reaches a points milestone (every 100 points). Recipients explicitly consented to receive these alerts through internal company agreement. Messages are 100% transactional — they do NOT contain advertising or promotional content. Recipients can opt out by replying STOP to the SMS at any time, or by asking the administrator to remove them from the list at /admin/sms. Estimated frequency: 5-15 messages per recipient per business day."
      },
      {
        h: "4. Sharing with third parties",
        p: "We share minimal, strictly operational information with the following service providers: (a) Resend (transactional email delivery); (b) Twilio (SMS delivery); (c) Anthropic / Google / OpenAI via Emergent LLM (AI text processing, no training on your data); (d) MongoDB (data storage on secure infrastructure). Each operates under its own privacy terms and only receives the data necessary to fulfill its role."
      },
      {
        h: "5. Data retention",
        p: "Account data is kept while your account remains active. Login sessions (cookies and tokens) expire automatically between 7 and 90 days. SMS and email logs are retained for 12 months for audit purposes. Upon account deactivation you may request full deletion of your personal data (except training records the company is required to retain for compliance reasons)."
      },
      {
        h: "6. Your rights",
        p: "You have the right to: (a) access your stored data; (b) correct it if outdated or inaccurate; (c) request deletion of your account; (d) opt out of SMS (reply STOP or ask the administrator to remove you). To exercise any right contact: antonio@canalesfranchise.com."
      },
      {
        h: "7. Security",
        p: "We apply reasonable technical measures: bcrypt-hashed passwords, HttpOnly + Secure cookies, mandatory HTTPS, rate-limiting against brute-force attacks, audit logs for sensitive events. Despite these efforts, no online system is 100% impenetrable. In the event of a material breach we will notify affected users without undue delay."
      },
      {
        h: "8. Changes to this notice",
        p: "We may periodically update this privacy notice. The date at the top reflects the latest revision. If changes are significant we will notify you by email or in-platform."
      },
      {
        h: "9. Contact",
        p: "If you have questions about privacy or wish to exercise your rights, write to: Canales Franchise LLC · antonio@canalesfranchise.com"
      },
    ],
  },
};

export default function PrivacyPolicy() {
  const [locale, setLocale] = useState("en");
  const c = CONTENT[locale];

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-sm text-[#6B7280] hover:text-[#1B2E7A] inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Canales Franchise LLC
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 md:py-20">
        <p className="text-xs uppercase tracking-[0.22em] text-[#6B7280] mb-3">{c.eyebrow}</p>
        <h1 className="font-heading text-4xl md:text-5xl text-[#1A1A1A] mb-3">{c.title}</h1>
        <p className="text-xs text-[#6B7280] mb-8">{c.updated}</p>
        <p className="text-[#374151] leading-relaxed mb-10">{c.intro}</p>

        <div className="space-y-8">
          {c.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-heading text-xl text-[#1A1A1A] mb-2">{s.h}</h2>
              <p className="text-[#374151] leading-relaxed">{s.p}</p>
            </section>
          ))}
        </div>

        <footer className="mt-16 pt-8 border-t border-[#E5E7EB] text-xs text-[#6B7280] flex items-center justify-between flex-wrap gap-2">
          <p>© 2026 Canales Franchise LLC · {c.title}</p>
          <Link to="/terms" className="text-[#1B2E7A] hover:underline" data-testid="link-terms">
            {locale === "es" ? "Términos y Condiciones" : "Terms & Conditions"}
          </Link>
        </footer>
      </main>
    </div>
  );
}
